<template>
  <div class="event-catalog-participant">
    <div class="event-catalog-participant__section">
      <p class="event-catalog-participant__hint">
        {{ $t("event.catalog.modal.alphabetList.basicInfoHelp") }}
        <button class="button-clean text-hyperlink" @click="showCompanyModal">
          {{ $t("event.catalog.modal.alphabetList.basicInfoLink") }}
        </button>
      </p>
    </div>

    <div class="event-catalog-participant__section">
      <div
        class="input-label event-catalog-participant__company-stands event-catalog-participant__container-col _380"
      >
        <h2 class="event-catalog-participant__section title">
          {{ $t("event.catalog.modal.alphabetList.stands") }}
        </h2>
        <InputLabel>
          <template slot="label">
            {{ $t("event.catalog.modal.alphabetList.companyStand") }}
            <span class="asterisk">*</span>
          </template>

          <div
            v-for="stand in catalogStands"
            :key="`company-stands-${stand.id}`"
          >
            <div style="display: flex;">
              <input
                v-model="stand.value"
                class="input event-catalog-participant__company-address"
                @input="
                  stand.value = $event.target.value.toUpperCase();
                  handleStandInput(stand.id, stand.value);
                "
                maxlength="6"
                :disabled="!hasAccessToWrite"
              />
              <button
                v-if="catalogStands.length > 1"
                class="button-clean"
                style="margin-left: 10px; margin-top: -16px"
                @click="deleteCompanyStand(stand.id)"
              >
                <icon name="delete" width="16" height="16" />
              </button>
            </div>
            <span
              :key="`company-stands-error-${stand.id}`"
              v-if="invalidStands.includes(stand.id)"
              class="event-catalog-participant__company-stand-error"
            >
              {{ $t("event.catalog.modal.alphabetList.invalidCompanyStand") }}
            </span>
          </div>
        </InputLabel>

        <LinkIcon
          @click="addCompanyStand('')"
          icon="add"
          grey
          bold
          v-if="hasAccessToWrite"
          class="event-catalog-participant__add-address-btn"
        >
          {{ $t("event.catalog.modal.alphabetList.addExtraCompanyStand") }}
        </LinkIcon>
      </div>
      <h2 class="event-catalog-participant__section title">
        {{ $t("event.catalog.modal.alphabetList.basicInfo") }}
      </h2>
      <div class="event-catalog-participant__container basic-info">
        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyNameForBooklet") }}
            <span class="asterisk">*</span>
          </template>

          <input
            class="input"
            :disabled="!hasAccessToWrite"
            v-model="companyName"
            @keydown="isCompanyNameNotificationDeleted = false"
          />

          <template slot="notification" v-if="companyNameShowNotification">
            <span>
              <span style="font-weight: bold;"> {{ companyName[0] }} </span> —
              {{ $t("event.catalog.modal.alphabetList.letterNote") }}
            </span>

            <button
              class="delete"
              @click="isCompanyNameNotificationDeleted = true"
            >
              <icon name="delete" />
            </button>
          </template>
        </InputLabel>

        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{
              $t("event.catalog.modal.alphabetList.companyCountryForBooklet")
            }}
            <span class="asterisk">*</span>
          </template>

          <template slot="label-right">
            <EventModalCatalogExample
              :images="companyCountryForBookletImages"
            />
          </template>

          <Select
            v-model="companyCountryForBooklet"
            :options="countries"
            :disabled="!hasAccessToWrite"
          />
        </InputLabel>
      </div>
    </div>

    <div class="event-catalog-participant__section">
      <h3 class="event-catalog-participant__title">
        <EventModalCatalogExample
          class="event-catalog-participant__title-pull-right"
          :images="additionalInfoImages"
          :explicit-label="
            $t('event.catalog.modal.alphabetList.additionalFields')
          "
        />
      </h3>

      <div class="event-catalog-participant__container">
        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyNameEng") }}
            <span class="asterisk">*</span>
          </template>

          <input
            type="text"
            v-model="companyNameEng"
            :disabled="!hasAccessToWrite"
            :class="{
              error: !validCompanyNameEng
            }"
            @focus="activeTooltipErrorCompanyNameEng = true"
            @blur="activeTooltipErrorCompanyNameEng = false"
          />

          <template
            slot="tooltip-error"
            v-if="!validCompanyNameEng && activeTooltipErrorCompanyNameEng"
          >
            {{ $t("event.catalog.modal.alphabetList.companyNameEngValid") }}
          </template>
        </InputLabel>

        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyOwnerType") }}
          </template>

          <input
            type="text"
            v-model="companyOwnerType"
            :disabled="!hasAccessToWrite"
            placeholder="OOO"
            maxlength="20"
          />
        </InputLabel>
      </div>

      <div class="event-catalog-participant__container">
        <div class="event-catalog-participant__container-col _380">
          <InputLabel class="event-catalog-participant__item">
            <template #label>
              {{ $t("event.catalog.modal.alphabetList.companyCountry") }}
              <span class="asterisk">*</span>
            </template>

            <Select
              v-model="companyCountry"
              :options="countries"
              :disabled="!hasAccessToWrite"
            />
          </InputLabel>

          <Checkbox
            v-if="isActiveRussianExporter"
            v-model="isRussianExporter"
            class="event-catalog-participant__item"
          >
            {{ $t("event.catalog.modal.alphabetList.companyRussianExporter") }}
          </Checkbox>
        </div>

        <div class="event-catalog-participant__container-col _380">
          <InputLabel>
            <template #label>
              {{ $t("event.companyProfile.modal.addressForm.address") }}
            </template>

            <input
              class="event-catalog-participant__company-address"
              type="text"
              v-model="companyAddresses[0].address"
              :disabled="!hasAccessToWrite"
              placeholder="111111, Москва, Москва, Московский пр., 12"
              @input="
                companyAddresses[0].address = companyAddresses[0].address.replace(
                  regex,
                  ''
                )
              "
            />
          </InputLabel>

          <InputLabel v-if="companyAddresses.length >= 2">
            <template #label>
              {{ $t("event.catalog.modal.alphabetList.extraCompanyAddress") }}

              <EventModalCatalogExample
                :label="false"
                :images="extraAddressAlphabeticalList.images"
                style="transform: translateY(3px); z-index: 1;"
              />
            </template>

            <div
              v-for="item in companyAddresses.slice(1)"
              :key="item.id"
              style="display: flex;"
            >
              <input
                placeholder="111111 Москва, Московский пр., 12"
                v-model="item.address"
                class="input event-catalog-participant__company-address"
                :disabled="!hasAccessToWrite"
                @input="item.address = item.address.replace(regex, '')"
              />

              <button
                class="button-clean"
                style="margin-left: 10px; margin-top: -16px"
                @click="deleteCompanyAddress(item.id)"
              >
                <icon name="delete" width="16" height="16" />
              </button>
            </div>
          </InputLabel>

          <LinkIcon
            @click="addCompanyAddress('')"
            icon="add"
            grey
            bold
            v-if="hasAccessToWrite && !isAddCompanyDisabled"
            class="event-catalog-participant__add-address-btn"
          >
            {{ $t("event.catalog.modal.alphabetList.addExtraAddress") }}
            <template v-if="additionalCompanyAddressPrice.errorPrice">
              (<ErrorPriceCatalog />)
            </template>
            <template v-else>
              ({{ additionalCompanyAddressPrice.price }} {{ currencySymbol }})
            </template>
          </LinkIcon>
        </div>
      </div>

      <div class="event-catalog-participant__container">
        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyWebsite") }}
          </template>

          <input
            type="text"
            v-model="companySite"
            placeholder="example.ru"
            :disabled="!hasAccessToWrite"
          />
        </InputLabel>

        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyEmail") }}
          </template>

          <input
            type="email"
            v-model="companyEmail"
            placeholder="info@example.ru"
            :disabled="!hasAccessToWrite"
          />
        </InputLabel>
      </div>

      <div class="event-catalog-participant__container">
        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyPhone") }}
          </template>

          <input
            type="tel"
            v-model="companyPhone"
            :disabled="!hasAccessToWrite"
            placeholder="+7 900 000 00 00"
          />
        </InputLabel>

        <InputLabel class="event-catalog-participant__container-col _380">
          <template #label>
            {{ $t("event.catalog.modal.alphabetList.companyFax") }}
          </template>

          <input
            type="tel"
            v-model="companyFax"
            :disabled="!hasAccessToWrite"
            placeholder="+7 900 000 00 00"
          />
        </InputLabel>
      </div>

      <h2 class="event-catalog-participant__section title">
        {{ $t("event.catalog.modal.alphabetList.companyInfoTitle") }}
      </h2>
      <Notification
        class="event-catalog-participant__section notification"
        icon="warning-border--no-color"
      >
        {{
          event.is_not_resident
            ? $t("event.catalog.modal.alphabetList.companyInfoCounting")
            : $t(
                "event.catalog.modal.alphabetList.companyInfoCountingIsResident"
              )
        }}
        {{ companyInfoPlaceholder }}
      </Notification>
      <div class="event-catalog-participant__container">
        <div class="event-catalog-participant__container-col _780">
          <InputLabel error-position-static>
            <template #label>
              {{ isResidentFieldRuLabel }}
            </template>
            <text-area-speller
              v-model="companyInfo"
              :name="companyInfo"
              :placeholder="
                !event.is_not_resident ? companyInfoPlaceholder : ''
              "
              :disabled="!hasAccessToWrite"
              @input="onCompanyInfoChange"
            />
            <template slot="error" v-if="errors.has('companyInfo')">
              {{ $t(errors.first("companyInfo")) }}
            </template>
          </InputLabel>
        </div>
      </div>

      <div class="event-catalog-participant__container">
        <InputLabel
          error-position-static
          class="event-catalog-participant__container-col _780"
        >
          <template #label>
            {{ isResidentFieldEngLabel }}
          </template>

          <div style="position: relative;">
            <textarea-autosize
              name="companyInfoEng"
              class="input textarea"
              v-model="companyInfoEng"
              :disabled="!hasAccessToWrite"
              :min-height="90"
              :placeholder="event.is_not_resident ? companyInfoPlaceholder : ''"
              @input="onCompanyInfoEngChange"
            ></textarea-autosize>
            <span
              class="textarea-symbol__amount"
              v-show="companyInfoEng.trim().length"
              >{{ companyInfoEng.trim().length }}</span
            >
          </div>
          <template slot="error" v-if="errors.has('companyInfoEng')">
            {{ $t(errors.first("companyInfoEng")) }}
          </template>
        </InputLabel>
      </div>
      <div
        class="company-info-cost__info"
        v-if="isShowCompanyInfoLengthWarning"
      >
        <p class="event-catalog-participant__extra-cost">
          {{ $t("event.catalog.modal.alphabetList.totalSymbolsAmount") }}
          <span>{{ longestTextFieldCompanyInfoLength }}</span>
        </p>
        <p class="event-catalog-participant__extra-cost">
          {{ $t("event.catalog.modal.alphabetList.freeSymbols") }}
          <span> {{ freeCompanyInfoLength }}</span>
        </p>
        <p
          class="event-catalog-participant__extra-cost"
          v-if="numberOfExtraSymbols > 0"
        >
          {{ $t("event.catalog.modal.alphabetList.extraSymbols") }}
          <span
            >{{ numberOfExtraSymbols }} ({{
              priceForAdditionalCharacters + currencySymbol
            }})</span
          >
        </p>
      </div>
      <div
        class="event-catalog-participant__container"
        v-if="isMark && event.company.country.name_rus === 'РОССИЯ'"
      >
        <div class="event-catalog-participant__container-col _780">
          <Checkbox v-model="companyIsMark" :disabled="!hasAccessToWrite">
            {{ markValue }}
          </Checkbox>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import shortid from "shortid";
import apiCatalog from "@/api/catalog";
import modalHolder from "@/helpers/modalHolder";
import eventCatalogHelper from "@/helpers/eventCatalog";
import { asianArabicRegex } from "@/helpers/constants/regex";
import TextAreaSpeller from "@/components/Custom/TextAreaSpeller.vue";
import ErrorPriceCatalog from "@/components/Modal/Catalog/ErrorPriceCatalog.vue";
import {
  LinkIcon,
  Checkbox,
  InputLabel,
  Notification,
  Select
} from "@v2/shared/ui/components";
import EventModalCatalogExample from "./EventModalCatalogExample.vue";

export default {
  name: "EventModalCatalogParticipantsListForm",

  components: {
    Notification,
    TextAreaSpeller,
    ErrorPriceCatalog,
    InputLabel,
    Select,
    LinkIcon,
    Checkbox,

    EventModalCatalogExample
  },

  data: () => ({
    isCompanyNameNotificationDeleted: false,
    companyCountryForBookletImages: ["3-2-spisok-po-stranam.jpg"].map(
      x => `/static/catalog-order/${x}`
    ),
    additionalInfoImages: ["3-3-dopolnitelnaya-stroka-v-opisanii.jpg"].map(
      x => `/static/catalog-order/${x}`
    ),
    activeTooltipErrorCompanyNameEng: false,
    companyInfoDebounce: null,
    companyInfoEngDebounce: null,
    isTooltipCompanyInfoShown: false,
    regex: asianArabicRegex
  }),

  computed: {
    ...mapGetters("event", ["hasAccessToWrite", "eventId"]),

    ...mapState("event", ["event"]),
    ...mapState("eventCatalog", ["step", "status", "alphabetList"]),

    ...mapGetters("eventCatalog", [
      "freeCompanyInfoLength",
      "companyInfoStringLength",
      "companyInfoAdditionalStringPrice",

      "additionalCompanyAddressPrice",
      "extraAddressAlphabeticalList",
      "catalogStands",
      "isCompanyExporterFlagAllowed",
      "isMark",
      "markValue",
      "currencySymbol",
      "getOrderDataFromCatalog"
    ]),
    ...mapGetters({ countries: "eventCatalog/getCountryList" }),
    companyInfoPlaceholder() {
      return this.$t(
        "event.catalog.modal.alphabetList.companyInfoPlaceholder",
        {
          freeAmount: this.freeCompanyInfoLength,
          stringLength: this.companyInfoStringLength,
          price:
            this.companyInfoAdditionalStringPrice &&
            this.companyInfoAdditionalStringPrice.errorPrice
              ? "N/A"
              : this.companyInfoAdditionalStringPrice.price,
          currencySymbol: this.currencySymbol
        }
      );
    },
    isResidentFieldRuLabel() {
      return !this.event?.is_not_resident
        ? this.$t("event.catalog.modal.alphabetList.companyInfoResident")
        : this.$t("event.catalog.modal.alphabetList.companyInfo");
    },
    isResidentFieldEngLabel() {
      return !this.event?.is_not_resident
        ? this.$t("event.catalog.modal.alphabetList.companyInfoEngResident")
        : this.$t("event.catalog.modal.alphabetList.companyInfoEng");
    },
    companyNameShowNotification() {
      return (
        this.companyName.length >= 1 && !this.isCompanyNameNotificationDeleted
      );
    },

    isAddCompanyDisabled() {
      return !this.companyAddresses.find(x => x.address?.trim()?.length);
    },

    companyName: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyName || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyName",
          value
        });
      }
    },

    companyNameEng: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyNameEng || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyNameEng",
          value
        });
      }
    },

    validCompanyNameEng() {
      const regexCompanyNameEng = /[а-яА-ЯЁ]/gi;
      return !regexCompanyNameEng.test(this.companyNameEng);
    },

    companySite: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companySite || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companySite",
          value
        });
      }
    },

    companyEmail: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyEmail || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyEmail",
          value
        });
      }
    },

    companyPhone: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyPhone || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyPhone",
          value
        });
      }
    },

    companyFax: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyFax || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyFax",
          value
        });
      }
    },

    companyInfo: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyInfo || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyInfo",
          value: value.trim()
        });
      }
    },

    companyInfoEng: {
      get() {
        return this.$store.state.eventCatalog.alphabetList.companyInfoEng || "";
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyInfoEng",
          value: value.trim()
        });
      }
    },

    companyCountry: {
      get() {
        return (
          this.$store.state.eventCatalog.alphabetList.companyCountry || null
        );
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyCountry",
          value
        });
      }
    },

    isRussianExporter: {
      get() {
        return (
          this.$store.state.eventCatalog.alphabetList.companyRussianExporter ||
          false
        );
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyRussianExporter",
          value
        });
      }
    },

    isActiveRussianExporter() {
      const russianCountry = this.countries.find(
        el =>
          el.title.toLowerCase() === "россия" ||
          el.title.toLowerCase() === "russia"
      );
      const isRussian =
        russianCountry &&
        russianCountry.value === this.companyCountryForBooklet;

      // Если выбрана Российская Федерация
      return isRussian && this.isCompanyExporterFlagAllowed;
    },

    isCountryRussiaSelected() {
      const russianCountry = this.countries.find(
        el =>
          el.title.toLowerCase() === "россия" ||
          el.title.toLowerCase() === "russia"
      );
      if (!russianCountry) return false;

      return russianCountry.value === this.companyCountry;
    },

    isCatalogOrderRuLocale() {
      return this.countries?.find(
        country => country.value === this.companyCountry
      )?.isCatalogOrderRuLocale;
    },
    longestTextFieldCompanyInfoLength() {
      return this.event.is_not_resident
        ? this.companyInfoEng.length
        : this.companyInfo.length;
    },
    numberOfExtraSymbols() {
      return (
        this.longestTextFieldCompanyInfoLength - this.freeCompanyInfoLength
      );
    },
    isShowCompanyInfoLengthWarning() {
      return (
        (!this.event.is_not_resident && this.companyInfo.length) ||
        (this.event.is_not_resident && this.companyInfoEng.length)
      );
    },
    companyCountryForBooklet: {
      get() {
        return (
          this.$store.state.eventCatalog.alphabetList
            .companyCountryForBooklet || ""
        );
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyCountryForBooklet",
          value
        });

        if (!this.isActiveRussianExporter) {
          this.isRussianExporter = false;
        }
      }
    },

    companyOwnerType: {
      get() {
        return (
          this.$store.state.eventCatalog.alphabetList.companyOwnerType || ""
        );
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyOwnerType",
          value
        });
      }
    },
    companyAddresses: {
      get() {
        let companyAddresses = this.$store.state.eventCatalog.alphabetList
          .companyAddresses;

        if (!Array.isArray(companyAddresses) || !companyAddresses.length) {
          if (typeof companyAddresses === "undefined") companyAddresses = [];
          companyAddresses.push({ id: shortid.generate(), address: "" });
        }

        return companyAddresses;
      }
    },

    companyIsMark: {
      get() {
        return !!this.$store.state.eventCatalog.alphabetList.companyIsMark;
      },

      set(value) {
        this.UPDATE_ALPHABET_LIST_FIELD({
          field: "companyIsMark",
          value
        });
      }
    },
    priceForAdditionalCharacters() {
      return (
        Math.ceil(
          Math.max(
            this.longestTextFieldCompanyInfoLength - this.freeCompanyInfoLength,
            0
          ) / this.companyInfoStringLength
        ) * this.companyInfoAdditionalStringPrice.price
      );
    },

    invalidStands: {
      get() {
        return this.$store.state.eventCatalog.invalidStands;
      }
    }
  },

  methods: {
    ...mapMutations("eventCatalog", ["UPDATE_ALPHABET_LIST_FIELD"]),
    ...mapMutations("eventCatalog", ["ADD_INVALID_STAND"]),
    ...mapActions("eventCatalog", ["loadCountryList"]),
    ...mapMutations("eventCatalog", [
      "DELETE_INVALID_STAND",
      "SET_STANDS_ON_EMPTY_CATALOG"
    ]),
    ...mapMutations("event", ["UPDATE_CATALOG_STATUS"]),

    handleStandInput(id, value) {
      if (/^[a-zA-Z0-9-]*$/.test(value) === false) {
        this.ADD_INVALID_STAND(id);
      } else {
        this.DELETE_INVALID_STAND(id);
      }
    },
    addCompanyStand(value = "") {
      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyStands",
        value: [...this.catalogStands, { id: shortid.generate(), value }]
      });
    },

    deleteCompanyStand(id) {
      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyStands",
        value: this.catalogStands.filter(item => item.id !== id)
      });
    },

    addCompanyAddress(address = "") {
      this.companyAddresses.push({
        id: shortid.generate(),
        address
      });

      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyAddresses",
        value: this.companyAddresses
      });
    },

    deleteCompanyAddress(id) {
      const index = this.companyAddresses.findIndex(x => x.id === id);

      if (index === -1) {
        return;
      }

      this.companyAddresses.splice(index, 1);
    },

    showCompanyModal() {
      modalHolder.clear();

      this.$modal.hide("event-modal-catalog");
      this.$modal.show("event-modal-company-profile");
    },

    onCompanyInfoChange(value) {
      clearTimeout(this.companyInfoDebounce);
      this.companyInfoDebounce = setTimeout(() => {
        if (value && eventCatalogHelper.isCompanyInfoFalseSymbols(value)) {
          this.errors.add({
            field: "companyInfo",
            msg: "event.catalog.modal.alphabetList.errors.companyInfoRus"
          });
        } else {
          this.errors.remove("companyInfo");
        }
      }, 500);
    },

    onCompanyInfoEngChange(value) {
      clearTimeout(this.companyInfoEngDebounce);
      this.companyInfoEngDebounce = setTimeout(() => {
        if (
          value &&
          eventCatalogHelper.isCompanyInfoFalseSymbols(value, "eng")
        ) {
          this.errors.add({
            field: "companyInfoEng",
            msg: "event.catalog.modal.alphabetList.errors.companyInfoEng"
          });
        } else {
          this.errors.remove("companyInfoEng");
        }
      }, 500);
    },

    async nextStep() {
      const isValidCompanyInfo = this.companyInfo
        ? !eventCatalogHelper.isCompanyInfoFalseSymbols(this.companyInfo)
        : true;
      const isValidCompanyInfoEng = this.companyInfoEng
        ? !eventCatalogHelper.isCompanyInfoFalseSymbols(
            this.companyInfoEng,
            "eng"
          )
        : true;

      if (!isValidCompanyInfo || !isValidCompanyInfoEng) return;

      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyAddresses",
        value: this.companyAddresses.filter(x => !!x.address?.trim().length)
      });

      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyStands",
        value: this.catalogStands
          ? this.catalogStands.filter(x => !!x.value.trim().length)
          : []
      });

      if (this.event.catalog_order_id) {
        await apiCatalog.update(
          this.eventId,
          this.getOrderDataFromCatalog,
          this.event.catalog_order_id
        );
      } else {
        const response = await apiCatalog.create(
          this.eventId,
          this.getOrderDataFromCatalog
        );
        if (response && response.data) {
          this.$store.dispatch("event/setOrderId", response.data.id);
        }
      }
    }
  },

  async beforeMount() {
    await this.loadCountryList();

    if (!this.event.catalog_order_id) {
      this.SET_STANDS_ON_EMPTY_CATALOG();
      const details = this.event?.company || {};
      const fields = {
        companyName: "name_rus",
        companyNameEng: "name_eng",
        companySite: "website",
        companyEmail: "email",
        companyPhone: "phone",
        companyFax: "fax"
      };

      Object.keys(fields).forEach(key => {
        if (!this[key] && details[fields[key]]) {
          this[key] = details[fields[key]];
        }
      });

      if (details.country) {
        this.companyCountryForBooklet = details.country.id;
        this.companyCountry = details.country.id;
      }

      if (this.companyAddresses.length === 0) {
        this.addCompanyAddress(details.address || "");
      }
      this.companyAddresses[0].address = details.address;

      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "companyAddresses",
        value: this.companyAddresses
      });
    }
  },
  watch: {
    longestTextFieldCompanyInfoLength(value) {
      this.UPDATE_ALPHABET_LIST_FIELD({
        field: "longestTextFieldCompanyInfoLength",
        value
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-catalog-participant {
  &__hint {
    @include description();

    color: #6d7179;
    margin: 0 0 20px 0;

    a {
      @include hyperlink();
    }
  }

  &__title {
    @include h3();

    margin-bottom: 26px;

    display: flex;
    align-items: center;

    &-pull-right {
      margin-left: auto;
    }

    @include respond-down(md) {
      font-size: 14px;
    }
  }

  &__section {
    //margin-bottom: 50px;
    //
    //@include respond-down(md) {
    //  margin-bottom: 30px;
    //}
    &.title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      letter-spacing: normal;
      color: #262626;
      margin: 0 0 20px 0;
      padding: 0;
      display: flex;
      align-items: center;
      @include respond-down(md) {
        font-size: 16px;
      }
    }
    &.notification {
      margin-bottom: 20px;
    }
  }

  &__company-stands {
    margin-bottom: 35px;
  }

  &__company-stand-error {
    display: inline-block;
    color: red;
    margin-bottom: 20px;
  }

  &__container {
    display: flex;

    justify-content: space-between;

    margin-bottom: 30px;
    &.basic-info {
      margin-bottom: 0;
    }
    @include respond-down(md) {
      margin-bottom: 10px;
    }

    flex-wrap: wrap;

    &-col {
      &._380 {
        width: 380px;

        @include respond-down(lg) {
          width: 350px;
        }

        @include respond-down(md) {
          width: 100%;
        }
      }

      &._780 {
        width: 780px;

        @include respond-down(md) {
          width: 100%;
        }
      }

      @include respond-down(md) {
        &:not(:first-of-type) {
          margin-top: 10px;
        }
      }
    }
  }

  &__company-address {
    margin-bottom: 20px;
  }

  &__company-info-count {
    color: #6d7179;
    width: fit-content;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    position: relative;
    & button {
      position: absolute;
      top: -6px;
      right: -20px;
      background-color: transparent;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
    }
  }

  &__extra-cost {
    font-size: 14px;
    line-height: 1.33;
    color: #6d7179;
    font-weight: 500;
    margin: 0 0 5px 0;
    span {
      margin-left: 5px;
      font-size: 14px;
      font-weight: bold;
      color: #005197;
    }
  }

  .cn:first-letter {
    color: #005197;
    font-weight: bold;
    font-size: 14px;
  }

  &__add-address-btn {
    @include respond-down(md) {
      margin-top: -10px;
      margin-bottom: 5px;
      display: block;
    }
  }

  &__item {
    margin-top: 20px;

    @include respond-down(md) {
      margin-top: 10px;
    }
    &:first-child {
      margin-top: 0;
    }
  }
}
.speller-textarea__info {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  @include respond-down(md) {
    flex-direction: column;
    .event-catalog-participant__extra-cost {
      margin-left: 0;
    }
  }
}
.company-info-cost__info {
  margin-top: -10px;
}
.textarea-symbol__amount {
  position: absolute;
  right: 10px;
  bottom: 20px;
  font-weight: 600;
  color: #0066cc;
  z-index: 10;
}
</style>
